import { createFileRoute, Link } from '@tanstack/react-router';
import { buildCanonicalLink, buildMeta } from '@/lib/seo';
import { ssrForPublicOnly } from '@/lib/ssr-utils';

export const Route = createFileRoute('/privacy-policy/')({
  ssr: ssrForPublicOnly,
  head: () => ({
    meta: buildMeta({
      title: 'מדיניות פרטיות - Pricedrop',
      description: 'מדיניות הפרטיות של Pricedrop — כיצד אנו אוספים, משתמשים ומגנים על המידע שלכם.',
      canonicalUrl: '/privacy-policy',
    }),
    links: [buildCanonicalLink('/privacy-policy')],
  }),
  component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
  return (
    <div className="mx-auto max-w-4xl rounded-lg bg-card p-6 shadow-md">
      <h1 className="mb-6 text-3xl font-bold">מדיניות פרטיות</h1>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">מבוא</h2>
        <p>
          Pricedrop מכבד את פרטיות המשתמשים. מדיניות זו מסבירה אילו פרטים אנו אוספים, כיצד אנו
          משתמשים בהם וכיצד אנו מגנים עליהם. מדיניות זו מהווה חלק בלתי נפרד מ
          <Link to="/terms-of-use" className="text-primary hover:underline">
            תנאי השימוש
          </Link>{' '}
          של השירות.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">מידע שאנו אוספים</h2>

        <h3 className="mb-2 mt-4 text-lg font-medium">פרטי חשבון</h3>
        <p className="mb-3">
          בעת הרשמה אנו אוספים: שם, כתובת דוא&quot;ל, סיסמה (מאוחסנת בצורה מוצפנת), ובהתחברות דרך
          Google — פרטי הפרופיל הבסיסיים. ניתן להוסיף גם מספר טלפון, תמונת פרופיל, שפה ומדינה.
        </p>

        <h3 className="mb-2 mt-4 text-lg font-medium">ביקורות ותוכן</h3>
        <p className="mb-3">
          ביקורות שנכתבות כוללות דירוג, טקסט ותמונות שהמשתמש מעלה. ביקורות מוצגות באופן פומבי עם שם
          המשתמש ותמונת הפרופיל.
        </p>

        <h3 className="mb-2 mt-4 text-lg font-medium">התראות ומעקב</h3>
        <p className="mb-3">
          הגדרות התראות שהמשתמש יוצר (סוג התראה, מוצרים, סף מחיר) ופרטי ערוצי התקשורת שהמשתמש מחבר:
          כתובת דוא&quot;ל, מזהה Telegram, מספר WhatsApp.
        </p>

        <h3 className="mb-2 mt-4 text-lg font-medium">מידע טכני</h3>
        <p>
          במהלך השימוש בשירות נאסף מידע טכני הכולל: כתובת IP, סוג הדפדפן ומידע על שגיאות טכניות
          לצורך שיפור השירות.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">כיצד אנו משתמשים במידע</h2>
        <ul className="list-inside list-disc space-y-2">
          <li>אספקת השירות — ניהול חשבון, הצגת מעקבים, משלוח התראות.</li>
          <li>הצגת ביקורות — פרסום תוכן שהמשתמש כתב במסגרת דפי המוצרים.</li>
          <li>
            משלוח התראות — שליחת עדכוני מחיר בערוצים שהמשתמש בחר (דוא&quot;ל, Telegram, WhatsApp).
          </li>
          <li>שיפור השירות — ניתוח שגיאות טכניות ושיפור חוויית המשתמש.</li>
          <li>מניעת שימוש לרעה — הגנה מפני ספאם, בוטים והתנהגות זדונית.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">עוגיות ואחסון מקומי</h2>
        <ul className="list-inside list-disc space-y-2">
          <li>
            <strong>עוגיות הפעלה (Session cookies)</strong> — נדרשות לזיהוי המשתמש המחובר.
          </li>
          <li>
            <strong>אחסון מקומי (localStorage)</strong> — שמירת טיוטות ביקורות והעדפות ממשק בדפדפן
            המשתמש.
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">אבטחת מידע</h2>
        <p>
          אנו נוקטים אמצעים סבירים להגנה על המידע: סיסמאות מאוחסנות בצורה מוצפנת, התקשורת מוצפנת
          באמצעות HTTPS, ותמונות מאוחסנות בשירות אחסון מאובטח. עם זאת, אין מערכת שמבטיחה אבטחה
          מוחלטת.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="mb-4 text-2xl font-semibold">שינויים במדיניות</h2>
        <p>
          Pricedrop רשאי לעדכן מדיניות פרטיות זו מעת לעת. המשך השימוש בשירות לאחר עדכון המדיניות
          מהווה הסכמה למדיניות המעודכנת.
        </p>
      </section>

      <section>
        <h2 className="mb-4 text-2xl font-semibold">יצירת קשר</h2>
        <p>
          לשאלות בנוגע למדיניות הפרטיות, ניתן לפנות אלינו דרך{' '}
          <Link to="/contact" className="text-primary hover:underline">
            דף יצירת הקשר
          </Link>
          .
        </p>
      </section>
    </div>
  );
}
